import { useLingui } from "@lingui/react/macro";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import type { PaymentScheduleSummary } from "@offstreetparking/domain/value/payment-schedule";
import { toShortStateWithoutCountry } from "@offstreetparking/geography";
import { DateTime } from "luxon";
import { PaymentScheduleOverview } from "../../../../../dataDisplay/parkingDetails";
import { PriceBreakdown } from "../../../../../dataDisplay/parkingDetails/PriceBreakDown";
import type { PermitTypeDto } from "../../../../types";
type InvoiceDetailsProps = {
	permitType: PermitTypeDto;
	paymentSchedule?: PaymentScheduleSummary;
	time: {
		startDateTime: string;
		endDateTime?: string;
		timezone: string;
	};
	invoice?: {
		total: number;
		subtotal: number;
		taxAndFees: {
			description: string;
			longDescription?: string;
			percent?: number;
			fixedAmount?: number;
			total: number;
		}[];
		fixedRateItems?: {
			style: string;
			amount: number;
			units: number;
			total: number;
		}[];
	};
	vehicles?: {
		plate: string;
		state: string;
	}[];
	email?: string;
};
export const InvoiceDetails = ({
	permitType,
	vehicles,
	email,
	invoice,
	time,
	paymentSchedule,
}: InvoiceDetailsProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	return (
		<>
			<div className="space-y-4 px-2">
				{time.endDateTime &&
				DateTime.fromISO(time.startDateTime)
					.setZone(permitType.timezone)
					.hasSame(DateTime.fromISO(time.endDateTime).setZone(permitType.timezone), "day") ? (
					<div className="flex justify-between">
						<Text size="sm">{t`Date`}</Text>
						<Text size="sm">
							{formatDate({
								date: time.startDateTime,
								formatType: "Abbreviated_Date",
								timezone: permitType.timezone,
								language: locale,
							})}
						</Text>
					</div>
				) : (
					<>
						<div className="flex justify-between">
							<Text size="sm">{t`Start Date`}</Text>
							<Text size="sm">
								{formatDate({
									date: time.startDateTime,
									formatType: "Abbreviated_DateTime",
									timezone: permitType.timezone,
									language: locale,
								})}
							</Text>
						</div>
						<div className="flex justify-between">
							<Text size="sm">{t`End Date`}</Text>
							<Text size="sm">
								{time.endDateTime
									? formatDate({
											date: time.endDateTime,
											formatType: "Abbreviated_DateTime",
											timezone: permitType.timezone,
											language: locale,
										})
									: t({
											message: "Never Expires",
											comment: "End date of a permit that never expires",
										})}
							</Text>
						</div>
					</>
				)}

				{vehicles?.map((v, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: Safe index
					<div key={i} className="flex justify-between">
						<Text size="sm">{t`License Plate`}</Text>
						<Text size="sm">{t`${v.plate}, ${toShortStateWithoutCountry(v.state)}`}</Text>
					</div>
				))}

				{email && (
					<div className="flex justify-between">
						<Text size="sm">{t`Email receipt`}</Text>
						<Text size="sm">{email}</Text>
					</div>
				)}
			</div>

			<div className="border-black border-t">
				<PriceBreakdown invoice={invoice} country={permitType.company.address?.country ?? undefined} />
			</div>

			{paymentSchedule && (
				<div className="pt-4">
					<PaymentScheduleOverview
						overview={paymentSchedule}
						country={permitType.company.address?.country ?? undefined}
					/>
				</div>
			)}
		</>
	);
};
